// UTM parameter types and utilities

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  affiliate?: string;
}

const UTM_STORAGE_KEY = 'autoclipper_utm_params';

/**
 * Extract UTM parameters from URL search params
 */
export const extractUtmParams = (searchParams: URLSearchParams): UtmParams => {
  const utmParams: UtmParams = {};

  const utmKeys: (keyof UtmParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'affiliate',
  ];

  utmKeys.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  if (!utmParams.affiliate) {
    const shortAffiliate = searchParams.get('a');
    if (shortAffiliate) {
      utmParams.affiliate = shortAffiliate;
    }
  }

  return utmParams;
};

/**
 * Save UTM parameters to localStorage
 * Only saves if there are actual UTM params present
 */
export const saveUtmParams = (params: UtmParams): void => {
  if (Object.keys(params).length > 0) {
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    } catch (error) {
      console.error('Failed to save UTM params to localStorage:', error);
    }
  }
};

/**
 * Get UTM parameters from localStorage
 */
export const getUtmParams = (): UtmParams => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as UtmParams;
    }
  } catch (error) {
    console.error('Failed to read UTM params from localStorage:', error);
  }
  return {};
};

/**
 * Clear UTM parameters from localStorage
 */
export const clearUtmParams = (): void => {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear UTM params from localStorage:', error);
  }
};

/**
 * Capture UTM params from current URL and save them
 */
export const captureUtmParamsFromUrl = (): UtmParams => {
  const searchParams = new URLSearchParams(window.location.search);
  const utmParams = extractUtmParams(searchParams);

  if (Object.keys(utmParams).length > 0) {
    saveUtmParams(utmParams);
  }

  return utmParams;
};

